/* ============================================================
   Directional header reveal

   Hides the fixed header on scroll down, brings it back on scroll up.
   Vanilla on purpose - it needs to be running before jQuery is ready,
   since the header state is visible from the very first scroll event.

   All the interesting work here is in NOT reacting: to jitter, to
   overscroll bounce, to the top of the page, and to states where the
   header has to stay put.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  var header = document.querySelector('header.row.u-full-width');
  if (!header) return;

  /* Movement in one direction before the header flips state. Trackpads and
     momentum scrolling emit a pixel or two the other way constantly; without
     this the header flickers. */
  var FLIP_THRESHOLD = 8;
  /* The header is always visible this close to the top, so it never hides
     during the first few pixels of a scroll. */
  var TOP_ZONE = 24;

  var lastY = window.pageYOffset;
  var travelled = 0;
  var hidden = false;
  var ticking = false;
  var locks = 0;
  var active = false;
  var headerHeight = header.offsetHeight;
  var stickyObserver = null;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The product page holds its header put.

     Its media column is sticky at `top: var(--header-height) + 1.6rem` and
     the slider's height is measured from the same variable, so a header
     that slides away leaves the images anchored to a bar that is no longer
     there — and the space it vacates is never reclaimed, because neither
     offset recalculates on a transform. Pinning it is cheaper and steadier
     than teaching both to chase a moving header.

     The script still publishes window.PossenHeader below, so the cart
     popup's lock/unlock calls stay harmless no-ops rather than throwing. */
  var holdHeader = !!(document.body && document.body.classList.contains('template-product'));

  /* mmenu flags the open off-canvas menu on <html>, older builds on <body>. */
  function menuIsOpen() {
    return root.classList.contains('mm-opened') || document.body.classList.contains('mm-opened');
  }

  function setHidden(next) {
    if (next === hidden) return;
    hidden = next;
    travelled = 0;
    if (next) {
      root.setAttribute('data-header-hidden', '');
    } else {
      root.removeAttribute('data-header-hidden');
    }
  }

  function update() {
    ticking = false;

    var y = window.pageYOffset;

    /* iOS rubber-banding reports scroll positions outside the document.
       Treat them as noise so the header doesn't flip during the bounce. */
    var maxY = document.documentElement.scrollHeight - window.innerHeight;
    if (y < 0 || y > maxY) {
      lastY = y;
      return;
    }

    var delta = y - lastY;
    lastY = y;
    if (delta === 0) return;

    /* Near the top there is nothing to reclaim, and a header that hides on
       the first flick of the wheel feels twitchy. */
    if (y <= headerHeight + TOP_ZONE) {
      setHidden(false);
      return;
    }

    /* An open menu, or focus inside the header, outranks scroll direction -
       a keyboard user tabbing into the nav must not send it off-screen. */
    if (locks > 0 || menuIsOpen() || header.contains(document.activeElement)) {
      setHidden(false);
      return;
    }

    /* Accumulate travel in one direction; a reversal starts the count over. */
    if (delta > 0 !== travelled > 0) travelled = 0;
    travelled += delta;

    if (travelled > FLIP_THRESHOLD) setHidden(true);
    else if (travelled < -FLIP_THRESHOLD) setHidden(false);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  /* The collection filter bar rides along with the header once it is stuck,
     but must be left alone while it is still in the normal flow. */
  function watchStickyControls() {
    var controls = document.getElementById('collection-controls');
    if (!controls || !('IntersectionObserver' in window)) return;

    if (stickyObserver) stickyObserver.disconnect();
    stickyObserver = new IntersectionObserver(
      function (entries) {
        var top = entries[entries.length - 1].boundingClientRect.top;
        controls.classList.toggle('is-stuck', top <= headerHeight + 1);
      },
      {
        rootMargin: '-' + (headerHeight + 1) + 'px 0px 0px 0px',
        threshold: [0, 1]
      }
    );
    stickyObserver.observe(controls);
  }

  function measure() {
    headerHeight = header.offsetHeight;
    if (active) watchStickyControls();
  }

  function enable() {
    if (active) return;
    active = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    measure();
  }

  function disable() {
    active = false;
    window.removeEventListener('scroll', onScroll);
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }
    setHidden(false);
  }

  /* Anything that anchors UI to the header - the cart popup, the mobile
     menu - can pin it open while it is on screen. */
  window.PossenHeader = {
    reveal: function () {
      setHidden(false);
    },
    lock: function () {
      locks++;
      setHidden(false);
    },
    unlock: function () {
      locks = Math.max(0, locks - 1);
    }
  };

  /* Tabbing into a hidden header would otherwise focus something the shopper
     cannot see. */
  header.addEventListener('focusin', function () {
    setHidden(false);
  });

  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);

  if (!reduceMotion.matches && !holdHeader) enable();

  var onPreferenceChange = function () {
    if (reduceMotion.matches || holdHeader) disable();
    else enable();
  };
  if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onPreferenceChange);
  else if (reduceMotion.addListener) reduceMotion.addListener(onPreferenceChange);
})();
